import { useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useMessageThreads } from '@/hooks/useMessages';
import { getAvatarById } from '@/lib/avatar-options';
import { MessageThreadSheet } from '@/components/MessageThreadSheet';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MessagesInbox({ open, onClose }: Props) {
  const { data: threads, isLoading } = useMessageThreads();
  const [active, setActive] = useState<{ id: string; name: string } | null>(null);

  if (!open) return null;

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-end justify-center bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="animate-slide-up w-full max-w-[430px] max-h-[75vh] overflow-y-auto rounded-t-2xl border border-border bg-card"
          onClick={e => e.stopPropagation()}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-4 py-3">
            <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-primary" /> Messages
            </h3>
            <button onClick={onClose} className="text-muted-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-2">
            {isLoading ? (
              <p className="text-center text-xs text-muted-foreground font-body py-8">Loading...</p>
            ) : !threads?.length ? (
              <p className="text-center text-xs text-muted-foreground font-body py-8">
                No conversations yet. Reach out from a trade or community post.
              </p>
            ) : (
              threads.map(t => {
                const avatar = getAvatarById(t.partnerAvatar || '');
                return (
                  <button
                    key={t.partnerId}
                    onClick={() => setActive({ id: t.partnerId, name: t.partnerUsername })}
                    className="flex w-full items-center gap-3 rounded-xl p-3 text-left hover:bg-secondary/60 transition-colors"
                  >
                    {avatar ? (
                      <img src={avatar.src} alt={avatar.label} className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-base">
                        {t.partnerAvatar || '🧙'}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground font-body truncate">
                          {t.partnerUsername || 'Unknown'}
                        </span>
                        <span className="ml-auto text-[10px] text-muted-foreground font-body">
                          {timeAgo(t.lastAt)}
                        </span>
                      </div>
                      <p className={`text-xs truncate font-body ${t.unread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {t.lastMessage}
                      </p>
                    </div>
                    {t.unread > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                        {t.unread}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      <MessageThreadSheet
        open={!!active}
        onClose={() => setActive(null)}
        partnerId={active?.id ?? null}
        partnerUsername={active?.name ?? ''}
      />
    </>
  );
}
